<div class="dock">
  <!-- Only while a photo is loaded: how it sits on the board is the first
       thing anyone wants to change, so it stays out here. -->
  <div class="photo-bar" *ngIf="hasImage && !sessionMode">
    <div class="fit-toggle" role="group" aria-label="How the photo fits the board">
      <button *ngFor="let m of fitModes" type="button" [class.active]="m === fitMode"
              [attr.aria-pressed]="m === fitMode" (click)="fitModeChange.emit(m)">{{ fitLabel(m) }}</button>
    </div>
    <button type="button" class="photo-swap" (click)="importPhoto.emit()" aria-label="Choose a different photo">📷</button>
  </div>

  <!-- Everyday tools: the only three most people need. -->
  <div class="tool-row" role="group" aria-label="Drawing tools">
    <button *ngFor="let t of primaryTools" type="button" class="tool"
            [class.active]="t.id === tool" (click)="selectTool(t.id)"
            [attr.aria-pressed]="t.id === tool">
      <span class="ic" aria-hidden="true">{{ t.icon }}</span><span class="lbl">{{ t.label }}</span>
    </button>
    <button type="button" class="tool more-btn" [class.active]="usingExtraTool"
            (click)="toggleMore()" [attr.aria-expanded]="moreOpen" aria-label="More tools">
      <span class="ic" aria-hidden="true">⋯</span><span class="lbl">{{ moreLabel }}</span>
    </button>
  </div>

  <!-- Colours stay on screen: no digging for them. -->
  <div class="palette" role="group" aria-label="Colours">
    <button *ngFor="let c of palette" type="button" class="swatch" [class.sel]="c === color"
            (click)="pick.emit(c)" [attr.aria-label]="'Colour ' + c" [attr.aria-pressed]="c === color">
      <span class="dot" [style.background]="c"></span>
    </button>
    <label class="swatch custom" title="Any colour">
      <span class="dot rainbow" aria-hidden="true">+</span>
      <input type="color" [value]="color" (input)="pick.emit($any($event.target).value)" aria-label="Pick any colour" />
    </label>
  </div>

  <!-- In a session every stroke is already on the wall, so there is nothing to send. -->
  <div class="session-row" *ngIf="sessionMode">
    <div class="session-text">
      <span class="session-time">{{ countdown }} left</span>
      <span class="session-people">{{ peopleText }} &middot; no undo, paint over to fix</span>
    </div>
    <button type="button" class="leave" (click)="leaveSession.emit()">Leave</button>
  </div>

  <!-- The whole point of the page, always visible. -->
  <div class="send-row" *ngIf="!sessionMode">
    <button type="button" class="send" [disabled]="sending" (click)="send.emit()">
      <span class="send-label">{{ sending ? 'Sending…' : (multiFrame ? '📡 Send animation' : '📡 Send to board') }}</span>
    </button>
  </div>
</div>

<div class="more-scrim" *ngIf="moreOpen" (click)="closeMore()"></div>

<div class="more-sheet" *ngIf="moreOpen">
  <div class="grabber" aria-hidden="true"></div>
  <div class="more-head">
    <h2>More tools</h2>
    <button type="button" class="close" (click)="closeMore()" aria-label="Close more tools">✕</button>
  </div>

  <h3 class="sec-label">Shapes &amp; picking</h3>
  <div class="extra-grid">
    <button *ngFor="let t of extraTools" type="button" class="tool"
            [class.active]="t.id === tool" (click)="selectExtraTool(t.id)"
            [attr.aria-pressed]="t.id === tool">
      <span class="ic" aria-hidden="true">{{ t.icon }}</span><span class="lbl">{{ t.label }}</span>
    </button>
  </div>

  <h3 class="sec-label">Edit</h3>
  <div class="edit-row">
    <div class="dpad" role="group" aria-label="Nudge the drawing">
      <button type="button" (click)="nudge.emit({ dx: 0, dy: -1 })" aria-label="Shift up">↑</button>
      <div class="dpad-row">
        <button type="button" (click)="nudge.emit({ dx: -1, dy: 0 })" aria-label="Shift left">←</button>
        <button type="button" (click)="nudge.emit({ dx: 1, dy: 0 })" aria-label="Shift right">→</button>
      </div>
      <button type="button" (click)="nudge.emit({ dx: 0, dy: 1 })" aria-label="Shift down">↓</button>
    </div>
    <div class="edit-actions">
      <button type="button" [class.active]="selectMode" (click)="toggleSelect.emit()">🔲 Select</button>
      <button type="button" (click)="deselect.emit()">Deselect</button>
      <button type="button" (click)="copyFrame.emit()">⧉ Copy</button>
      <button type="button" [disabled]="!pasteEnabled" (click)="pasteFrame.emit()">📋 Paste</button>
    </div>
  </div>

  <ng-container *ngIf="multiFrame">
    <h3 class="sec-label">Animation</h3>
    <div class="anim-play">
      <button type="button" (click)="togglePreview.emit()">{{ playing ? '⏸ Stop' : '▶ Preview' }}</button>
      <label><input type="checkbox" [checked]="loop" (change)="loopChange.emit($any($event.target).checked)" /> Loop</label>
    </div>
  </ng-container>

  <h3 class="sec-label">Photo</h3>
  <div class="import-row">
    <button type="button" class="import-btn" (click)="importPhoto.emit()">📷 {{ hasImage ? 'Choose a different photo' : 'Import photo' }}</button>
  </div>
  <div class="photo-adjust" *ngIf="hasImage">
    <label class="adj-row">
      <span>Brightness</span>
      <input type="range" min="-100" max="100" [value]="imgBrightness"
             (input)="imgBrightnessChange.emit(+$any($event.target).value)" />
      <span class="adj-val">{{ imgBrightness }}</span>
    </label>
    <label class="adj-row">
      <span>Contrast</span>
      <input type="range" min="-100" max="100" [value]="imgContrast"
             (input)="imgContrastChange.emit(+$any($event.target).value)" />
      <span class="adj-val">{{ imgContrast }}</span>
    </label>
    <label class="adj-row">
      <span>Saturation</span>
      <input type="range" min="-100" max="100" [value]="imgSaturation"
             (input)="imgSaturationChange.emit(+$any($event.target).value)" />
      <span class="adj-val">{{ imgSaturation }}</span>
    </label>
    <ng-container *ngIf="fitMode === 'cover'">
      <label class="adj-row">
        <span>Crop ↔</span>
        <input type="range" min="-1" max="1" step="0.05" [value]="cropX"
               (input)="cropXChange.emit(+$any($event.target).value)" />
        <span class="adj-val">{{ cropX | number:'1.0-2' }}</span>
      </label>
      <label class="adj-row">
        <span>Crop ↕</span>
        <input type="range" min="-1" max="1" step="0.05" [value]="cropY"
               (input)="cropYChange.emit(+$any($event.target).value)" />
        <span class="adj-val">{{ cropY | number:'1.0-2' }}</span>
      </label>
    </ng-container>
    <button type="button" class="reset-btn" (click)="resetImport.emit()">Reset</button>
  </div>

  <h3 class="sec-label">Board</h3>
  <label class="bright">
    <span class="bright-label">Brightness <strong>{{ brightness }}</strong></span>
    <input type="range" min="5" max="50" [value]="brightness" (input)="onBrightness($any($event.target).value)" />
  </label>

  <div class="recent" *ngIf="recentColors?.length">
    <h3 class="sec-label">Recent colours</h3>
    <div class="palette">
      <button *ngFor="let c of recentColors" type="button" class="swatch" [class.sel]="c === color"
              (click)="pick.emit(c)" [attr.aria-label]="'Colour ' + c">
        <span class="dot" [style.background]="c"></span>
      </button>
    </div>
  </div>

  <button type="button" class="clear-btn" (click)="clear.emit()">🗑️ Clear drawing</button>
</div>
